import { Link } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"

interface Option {
  id: number
  name: string
}

interface CustomerFormData {
  company_name: string
  first_name: string
  last_name: string

  address: string
  address2: string
  city: string
  state: string
  zip: string
  office_phone: string
  cell_phone: string
  home_phone: string
  fax: string
  email: string
  website: string
  notes: string
  internal_notes: string
  client_type_id: number | null
  sales_rep_id: number | null
}

interface ReferralResult {
  id: string
  type: string
  customer_id?: number
  vendor_id?: number
  label: string
}

interface ReferringAttorney {
  client_type_id: number | null
  company_name: string
  first_name: string
  last_name: string
  phone: string
  email: string
}

interface Props {
  data: CustomerFormData
  setData: (key: keyof CustomerFormData, value: string | number | null) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  client_types: Option[]
  users: Option[]
  referralString?: string
  onReferralChange?: (refString: string, label: string) => void
  initialReferralLabel?: string | null
  showQuickAdd?: boolean
  referringAttorney?: ReferringAttorney
  onReferringAttorneyChange?: (attorney: ReferringAttorney) => void
}

export default function CustomerForm({
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  client_types,
  users,
  referralString,
  onReferralChange,
  initialReferralLabel,
  showQuickAdd = false,
  referringAttorney,
  onReferringAttorneyChange,
}: Props) {
  const [referralQuery, setReferralQuery] = useState(initialReferralLabel ?? "")
  const [referralResults, setReferralResults] = useState<ReferralResult[]>([])
  const [showResults, setShowResults] = useState(false)
  const [showAddAttorney, setShowAddAttorney] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Initialize referral query from label when it changes (e.g., after server round-trip)
  /* eslint-disable react-hooks/set-state-in-effect -- Syncing prop to local state when server provides updated label */
  useEffect(() => {
    if (initialReferralLabel) {
      setReferralQuery(initialReferralLabel)
    }
  }, [initialReferralLabel])
  /* eslint-enable react-hooks/set-state-in-effect */

  const searchReferrals = useCallback((term: string) => {
    if (term.length < 3) {
      setReferralResults([])
      setShowResults(false)
      return
    }

    fetch(`/customers/referral_search?q=${encodeURIComponent(term)}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data: ReferralResult[]) => {
        setReferralResults(data)
        setShowResults(true)
      })
      .catch(() => {
        setReferralResults([])
      })
  }, [])

  const handleReferralInput = (value: string) => {
    setReferralQuery(value)
    // Clear the selection when the user types
    if (onReferralChange && referralString) {
      onReferralChange("", "")
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchReferrals(value), 300)
  }

  const selectReferral = (result: ReferralResult) => {
    setReferralQuery(result.label)
    setShowResults(false)
    if (onReferralChange) {
      onReferralChange(result.id, result.label)
    }
  }

  const clearReferral = () => {
    setReferralQuery("")
    setShowResults(false)
    if (onReferralChange) {
      onReferralChange("", "")
    }
  }

  // Close results dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowResults(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <form onSubmit={onSubmit} className="max-w-4xl">
      <div className="mb-6 grid gap-6 md:grid-cols-2">
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Company Information</h2>

          <div className="mb-4">
            <label htmlFor="company_name" className="mb-1 block font-medium">
              Company Name
            </label>
            <input
              type="text"
              id="company_name"
              value={data.company_name}
              onChange={(e) => setData("company_name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={150}
            />
            {errors?.company_name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.company_name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="mb-1 block font-medium">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="last_name" className="mb-1 block font-medium">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="client_type_id"
                className="mb-1 block font-medium"
              >
                Client Type
              </label>
              <select
                id="client_type_id"
                value={data.client_type_id ?? ""}
                onChange={(e) =>
                  setData(
                    "client_type_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {client_types.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="sales_rep_id" className="mb-1 block font-medium">
                Sales Rep
              </label>
              <select
                id="sales_rep_id"
                value={data.sales_rep_id ?? ""}
                onChange={(e) =>
                  setData(
                    "sales_rep_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Contact Information</h2>

          <div className="mb-4">
            <label htmlFor="office_phone" className="mb-1 block font-medium">
              Office Phone
            </label>
            <input
              type="tel"
              id="office_phone"
              value={data.office_phone}
              onChange={(e) => setData("office_phone", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="cell_phone" className="mb-1 block font-medium">
                Cell Phone
              </label>
              <input
                type="tel"
                id="cell_phone"
                value={data.cell_phone}
                onChange={(e) => setData("cell_phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="home_phone" className="mb-1 block font-medium">
                Home Phone
              </label>
              <input
                type="tel"
                id="home_phone"
                value={data.home_phone}
                onChange={(e) => setData("home_phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="fax" className="mb-1 block font-medium">
              Fax
            </label>
            <input
              type="tel"
              id="fax"
              value={data.fax}
              onChange={(e) => setData("fax", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="mb-1 block font-medium">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={150}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="website" className="mb-1 block font-medium">
              Website
            </label>
            <input
              type="url"
              id="website"
              value={data.website}
              onChange={(e) => setData("website", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={150}
            />
          </div>
        </div>

        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Address</h2>

          <div className="mb-4">
            <label htmlFor="address" className="mb-1 block font-medium">
              Address
            </label>
            <input
              type="text"
              id="address"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="address2" className="mb-1 block font-medium">
              Address 2
            </label>
            <input
              type="text"
              id="address2"
              value={data.address2}
              onChange={(e) => setData("address2", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="city" className="mb-1 block font-medium">
                City
              </label>
              <input
                type="text"
                id="city"
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="state" className="mb-1 block font-medium">
                State
              </label>
              <input
                type="text"
                id="state"
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="zip" className="mb-1 block font-medium">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                value={data.zip}
                onChange={(e) => setData("zip", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={20}
              />
            </div>
          </div>
        </div>

        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Notes & Referral</h2>

          <div className="mb-4">
            <label htmlFor="notes" className="mb-1 block font-medium">
              General Notes
            </label>
            <textarea
              id="notes"
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={4}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="internal_notes" className="mb-1 block font-medium">
              Internal Notes
            </label>
            <textarea
              id="internal_notes"
              value={data.internal_notes}
              onChange={(e) => setData("internal_notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={4}
            />
          </div>

          {onReferralChange && (
            <div className="mb-4">
              <label
                htmlFor="referral_search"
                className="mb-1 block font-medium"
              >
                Referred by
              </label>
              <div className="relative">
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="referral_search"
                    ref={inputRef}
                    value={referralQuery}
                    onChange={(e) => handleReferralInput(e.target.value)}
                    onFocus={() => {
                      if (referralResults.length > 0) setShowResults(true)
                    }}
                    placeholder="Start typing to see list"
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                    autoComplete="off"
                  />
                  {referralString && (
                    <button
                      type="button"
                      onClick={clearReferral}
                      className="text-hpi-red shrink-0 px-2 text-sm hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {showResults && referralResults.length > 0 && (
                  <div
                    ref={resultsRef}
                    className="border-hpi-light-gray absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded border bg-white shadow-lg"
                  >
                    {referralResults.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        className="hover:bg-hpi-pale-blue w-full px-3 py-2 text-left text-sm"
                        onClick={() => selectReferral(r)}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {showQuickAdd && !referralString && (
                <div className="mt-1 text-sm">
                  Is the attorney not on this list?{" "}
                  <button
                    type="button"
                    onClick={() => setShowAddAttorney(!showAddAttorney)}
                    className="text-hpi-active-blue cursor-pointer font-bold"
                  >
                    Click here to add Attorney
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showQuickAdd &&
        showAddAttorney &&
        referringAttorney &&
        onReferringAttorneyChange && (
          <div className="border-hpi-yellow mb-6 rounded border bg-[#FFF6C5] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Quick Add Referring Attorney{" "}
                <span className="text-sm font-normal text-gray-600">
                  Leave fields blank if you don&apos;t know.
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setShowAddAttorney(false)}
                className="text-hpi-red text-lg font-bold"
              >
                &times;
              </button>
            </div>
            <p className="mb-3 text-sm text-gray-600">
              If you have more information about the referring attorney, use{" "}
              <a
                href="/customers/new"
                target="_blank"
                className="text-hpi-active-blue hover:underline"
              >
                Add Customer
              </a>{" "}
              instead.
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Company Type
                </label>
                <select
                  value={referringAttorney.client_type_id ?? ""}
                  onChange={(e) =>
                    onReferringAttorneyChange({
                      ...referringAttorney,
                      client_type_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                >
                  <option value="">Select...</option>
                  {client_types.map((ct) => (
                    <option key={ct.id} value={ct.id}>
                      {ct.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Firm/Company Name
                </label>
                <input
                  type="text"
                  value={referringAttorney.company_name}
                  onChange={(e) =>
                    onReferringAttorneyChange({
                      ...referringAttorney,
                      company_name: e.target.value,
                    })
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  First Name
                </label>
                <input
                  type="text"
                  value={referringAttorney.first_name}
                  onChange={(e) =>
                    onReferringAttorneyChange({
                      ...referringAttorney,
                      first_name: e.target.value,
                    })
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Last Name
                </label>
                <input
                  type="text"
                  value={referringAttorney.last_name}
                  onChange={(e) =>
                    onReferringAttorneyChange({
                      ...referringAttorney,
                      last_name: e.target.value,
                    })
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Phone</label>
                <input
                  type="tel"
                  value={referringAttorney.phone}
                  onChange={(e) =>
                    onReferringAttorneyChange({
                      ...referringAttorney,
                      phone: e.target.value,
                    })
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Email</label>
                <input
                  type="email"
                  value={referringAttorney.email}
                  onChange={(e) =>
                    onReferringAttorneyChange({
                      ...referringAttorney,
                      email: e.target.value,
                    })
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                  maxLength={50}
                />
              </div>
            </div>
          </div>
        )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href="/customers"
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
